/**
 * Note TOC Section List
 *
 * Pure presentational component displaying the list of note sections
 * - Header with "Note Sections" title
 * - Section items with selection checkboxes and active highlighting
 * - Footer with "Copy Selected" action
 *
 * This component is reusable across different contexts:
 * - Inside popover on hover (NoteTOC)
 * - Inside popover on click (NoteTOCButton)
 * - Potentially inline on ultrawide screens (future)
 */

import React from 'react';
import type { NoteSection } from '@features/notes';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeselectIcon from '@mui/icons-material/Deselect';
import { Box, Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { useEventTracking } from '@services/statsig';

import { SectionSelector } from '../SectionSelector';

export interface NoteTOCSectionListProps {
  /**
   * Array of note sections to display
   */
  sections: NoteSection[];

  /**
   * Currently active section ID (highlighted)
   */
  activeSectionId: string | null;

  /**
   * Array of selected section IDs (for copying)
   */
  selectedSectionIds: string[];

  /**
   * Handler for section click (navigation)
   */
  onSectionClick: (sectionId: string) => void;

  /**
   * Handler for toggling section selection
   */
  onToggleSelection: (sectionId: string) => void;

  /**
   * Handler for "Copy Selected" action
   */
  onCopySelected?: () => void;

  /**
   * Handler for "Deselect All" action
   */
  onDeselectAll?: () => void;

  /**
   * Note ID for analytics tracking
   */
  noteId?: string;

  /**
   * Encounter ID for analytics tracking
   */
  encounterId?: string;

  /**
   * Note section analytics tracker
   */
  noteSection?: ReturnType<typeof useEventTracking>['noteSection'];
}

/**
 * Pure UI component for note sections list
 * No state, no hooks - fully controlled
 */
export const NoteTOCSectionList: React.FC<NoteTOCSectionListProps> = ({
  sections,
  activeSectionId,
  selectedSectionIds,
  onSectionClick,
  onToggleSelection,
  onCopySelected,
  onDeselectAll,
  noteId,
  encounterId,
  noteSection,
}) => {
  const theme = useTheme();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
          Note Sections
        </Typography>
      </Box>

      <Divider />

      {/* Section List */}
      <Box sx={{ py: 0.5 }}>
        {sections.map(section => {
          const isActive = section.section_id === activeSectionId;
          const isSelected = selectedSectionIds.includes(section.section_id);

          return (
            <Box
              key={section.section_id}
              onClick={() => onSectionClick(section.section_id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                py: 0.75,
                cursor: 'pointer',
                bgcolor: isActive ? 'rgba(35, 131, 226, 0.08)' : 'transparent',
                '&:hover': {
                  bgcolor: isActive ? 'rgba(35, 131, 226, 0.12)' : 'rgba(55, 53, 47, 0.06)',
                },
                transition: theme.transitions.create('background-color', {
                  duration: theme.transitions.duration.shortest,
                }),
              }}
            >
              <SectionSelector
                sectionId={section.section_id}
                isSelected={isSelected}
                onToggle={sectionId => {
                  // Track selector click before toggling (action is the new state after click)
                  if (noteSection) {
                    const action = isSelected ? 'deselect' : 'select';
                    noteSection.selectorClicked({
                      section_id: sectionId,
                      section_name: section.name,
                      action,
                      source: 'toc',
                      note_id: noteId,
                      encounter_id: encounterId,
                    });
                  }

                  onToggleSelection(sectionId);
                }}
                size={16}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                  fontWeight: isActive ? 500 : 400,
                  ml: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {section.name}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Footer - Copy Selected & Deselect All */}
      {selectedSectionIds.length > 0 && (
        <>
          <Divider />

          {/* Copy Selected */}
          {onCopySelected && (
            <Box
              onClick={onCopySelected}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'rgba(55, 53, 47, 0.06)',
                },
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
              <Typography
                variant="body2"
                sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}
              >
                Copy Selected
              </Typography>
            </Box>
          )}

          {/* Deselect All */}
          {onDeselectAll && (
            <Box
              onClick={onDeselectAll}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'rgba(55, 53, 47, 0.06)',
                },
              }}
            >
              <DeselectIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
              <Typography
                variant="body2"
                sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}
              >
                Deselect All
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
